import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  csAdminUsers,
  csAdminForceAddGroup,
  csAdminForceRemoveGroup,
} from "../../api/admin.api";
import { csSession } from "../../auth/session";
import { csMySubscription } from "../../api/subscription.api";
import { csIsInWhatsappGroup } from "../../api/whatsapp.api";
import styles from "../../styles/admin.module.css";

const ADMIN_PHONE_UI = "0773134223";

function displayPhoneAz(phone) {
  const p = String(phone || "").replace(/\s+/g, "");
  if (!p) return "";
  if (p.startsWith("994") && p.length >= 12) return "0" + p.slice(3);
  if (p.startsWith("+994") && p.length >= 13) return "0" + p.slice(4);
  if (p.startsWith("0")) return p;
  return p;
}

function azVerifyStatusLabel(s) {
  const v = String(s || "").toUpperCase();
  switch (v) {
    case "REGISTERED":
      return "Qeydiyyat";
    case "PHONE_PENDING":
      return "Telefon gözləmədə";
    case "PHONE_VERIFIED":
      return "Telefon təsdiqləndi";
    case "DOCS_SUBMITTED":
      return "Sənədlər gözləmədə";
    case "APPROVED":
      return "Təsdiqləndi";
    case "REJECTED":
      return "Rədd edildi";
    default:
      return v || "-";
  }
}

function verifyPillClass(styles, s) {
  const v = String(s || "").toUpperCase();
  if (v === "APPROVED") return `${styles.pill} ${styles.pillOk}`;
  if (v === "REJECTED") return `${styles.pill} ${styles.pillBad}`;
  if (v === "DOCS_SUBMITTED" || v === "PHONE_VERIFIED") {
    return `${styles.pill} ${styles.pillWarn}`;
  }
  return styles.pill;
}

function pillOkBad(styles, ok) {
  return ok ? `${styles.pill} ${styles.pillOk}` : `${styles.pill} ${styles.pillBad}`;
}

export default function UsersList() {
  const nav = useNavigate();

  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [subMap, setSubMap] = useState({});
  const [subLoading, setSubLoading] = useState(false);

  const [groupMap, setGroupMap] = useState({});
  const [groupLoading, setGroupLoading] = useState(false);

  const [busyId, setBusyId] = useState(null);
  const [showFraudOnly, setShowFraudOnly] = useState(false);

  const logout = () => {
    csSession.clear?.();
    nav("/couriersub/login", { replace: true });
  };

  const goDetail = (id) => nav(`/couriersub/admin/users/${id}`);

  function normalizeSub(subPayload) {
    const st = String(subPayload?.status || "").toUpperCase();
    const hasSub = st === "ACTIVE";
    return { hasSub, status: st || "UNKNOWN" };
  }

  const loadSubscriptionsForUsers = async (users) => {
    setSubLoading(true);
    try {
      const tasks = users.map(async (u) => {
        const phone = u?.phone;
        if (!phone) {
          return {
            phone: "",
            hasSub: false,
            status: "UNKNOWN",
            err: "NO_PHONE",
          };
        }

        try {
          const payload = await csMySubscription(phone);
          const sub = payload?.data?.data ?? payload?.data ?? payload ?? null;
          return { phone, ...normalizeSub(sub) };
        } catch (e) {
          return {
            phone,
            hasSub: false,
            status: "ERROR",
            err: e?.message || "SUB_CHECK_FAILED",
          };
        }
      });

      const results = await Promise.all(tasks);
      const next = {};
      for (const r of results) {
        if (r.phone) next[r.phone] = r;
      }
      setSubMap(next);
    } finally {
      setSubLoading(false);
    }
  };

  const loadGroupsForUsers = async (users, { batchSize = 15 } = {}) => {
    setGroupLoading(true);

    const phones = users.map((u) => u?.phone).filter(Boolean);

    setGroupMap((prev) => {
      const next = { ...prev };
      for (const phone of phones) {
        next[phone] = { phone, inGroup: null };
      }
      return next;
    });

    try {
      for (let i = 0; i < phones.length; i += batchSize) {
        const batch = phones.slice(i, i + batchSize);

        const results = await Promise.all(
          batch.map(async (phone) => {
            try {
              const res = await csIsInWhatsappGroup(phone);
              const payload = res?.data?.data ?? res?.data ?? res ?? {};
              return { phone, inGroup: Boolean(payload?.inGroup) };
            } catch (e) {
              return {
                phone,
                inGroup: false,
                err:
                  e?.response?.data?.message ||
                  e?.message ||
                  "GROUP_CHECK_FAILED",
              };
            }
          })
        );

        setGroupMap((prev) => {
          const next = { ...prev };
          for (const r of results) {
            next[r.phone] = r;
          }
          return next;
        });

        if (i + batchSize < phones.length) {
          await new Promise((r) => setTimeout(r, 120));
        }
      }
    } finally {
      setGroupLoading(false);
    }
  };

  const load = async () => {
    setMsg("");
    setLoading(true);

    try {
      const res = await csAdminUsers();
      const payload = res?.data?.data ?? res?.data ?? res ?? [];
      const arr = Array.isArray(payload) ? payload : [];

      const filtered = arr.filter(
        (u) =>
          displayPhoneAz(u?.phone) &&
          displayPhoneAz(u?.phone) !== ADMIN_PHONE_UI
      );

      const sorted = [...filtered].sort((a, b) => {
        const aT = a?.createdAt ? new Date(a.createdAt).getTime() : null;
        const bT = b?.createdAt ? new Date(b.createdAt).getTime() : null;
        if (Number.isFinite(aT) && Number.isFinite(bT)) return bT - aT;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });

      setList(sorted);

      loadSubscriptionsForUsers(sorted);
      loadGroupsForUsers(sorted);
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Siyahı yüklənmədi.");
      setList([]);
      setSubMap({});
      setGroupMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function subHasByPhone(phone) {
    const r = subMap?.[phone];
    if (!r) return null;
    return !!r.hasSub;
  }

  function subLabelByPhone(phone) {
    const r = subMap?.[phone];
    if (!r) return subLoading ? "Yoxlanılır..." : "-";
    return r.hasSub ? "VAR" : "YOXDUR";
  }

  function groupStatusByPhone(phone) {
    const r = groupMap?.[phone];
    if (!r) return null;
    if (r.inGroup === null) return null;
    return !!r.inGroup;
  }

  function accountActiveLabel(u) {
    return u?.active === false ? "DEAKTİVDİR" : "AKTİVDİR";
  }

  function accountActiveBool(u) {
    return u?.active !== false;
  }

  const filteredList = useMemo(() => {
    if (!showFraudOnly) return list;

    return list.filter((u) => {
      const phone = u?.phone;
      const hasSub = subHasByPhone(phone);
      const inGroup = groupStatusByPhone(phone);

      return hasSub === false && inGroup === true;
    });
  }, [list, showFraudOnly, subMap, groupMap]);

  const total = useMemo(() => filteredList.length, [filteredList]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <div className={styles.brand}>MotoTaksi.az</div>
              <div className={styles.title}>Admin Panel – İstifadəçilər</div>
              <div className={styles.subtitle}>
                {loading || subLoading || groupLoading
                  ? "Yüklənir..."
                  : `Toplam: ${total} istifadəçi`}
              </div>
            </div>

            <div className={styles.nav}>
              <button
                className={styles.navBtn}
                onClick={load}
                disabled={loading || subLoading || groupLoading}
              >
                {loading || subLoading || groupLoading ? "Yüklənir..." : "Yenilə"}
              </button>

              <button
                className={`${styles.navBtn} ${
                  showFraudOnly ? styles.navBtnDanger : ""
                }`}
                onClick={() => setShowFraudOnly((v) => !v)}
                disabled={loading || subLoading || groupLoading}
              >
                {showFraudOnly ? "Hamısını göstər" : "Fırıldaqçıları göstər"}
              </button>

              <button
                className={`${styles.navBtn} ${styles.navBtnDanger}`}
                onClick={logout}
              >
                Çıxış
              </button>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {msg && <div className={styles.error}>{msg}</div>}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>ID</th>
                  <th className={styles.th}>Ad Soyad</th>
                  <th className={styles.th}>Telefon</th>
                  <th className={styles.th}>Yoxlama statusu</th>
                  <th className={styles.th}>Hesab</th>
                  <th className={styles.th}>Abunəlik</th>
                  <th className={styles.th}>WhatsApp</th>
                  <th className={styles.th}>Ətraflı</th>
                </tr>
              </thead>

              <tbody>
                {filteredList.length === 0 ? (
                  <tr>
                    <td className={styles.td} colSpan={8}>
                      <span className={styles.muted}>
                        {loading || subLoading || groupLoading
                          ? "Yüklənir..."
                          : "İstifadəçi tapılmadı."}
                      </span>
                    </td>
                  </tr>
                ) : (
                  filteredList.map((u) => {
                    const phone = u?.phone;
                    const accOk = accountActiveBool(u);
                    const hasSub = subHasByPhone(phone);
                    const inGroup = groupStatusByPhone(phone);

                    return (
                      <tr key={u.id}>
                        <td className={styles.td}>{u.id}</td>
                        <td className={styles.td}>
                          {u.firstName} {u.lastName}
                        </td>
                        <td className={styles.td}>{displayPhoneAz(phone)}</td>

                        <td className={styles.td}>
                          <span className={verifyPillClass(styles, u.verifyStatus)}>
                            {azVerifyStatusLabel(u.verifyStatus)}
                          </span>
                        </td>

                        <td className={styles.td}>
                          <span className={pillOkBad(styles, accOk)}>
                            {accountActiveLabel(u)}
                          </span>
                        </td>

                        <td className={styles.td}>
                          {hasSub === null ? (
                            <span className={styles.muted}>
                              {subLabelByPhone(phone)}
                            </span>
                          ) : (
                            <span className={pillOkBad(styles, hasSub)}>
                              {subLabelByPhone(phone)}
                            </span>
                          )}
                        </td>

                        <td className={styles.td}>
                          {inGroup === null ? (
                            <span className={styles.muted}>Yoxlanılır...</span>
                          ) : inGroup ? (
                            <button
                              className={`${styles.navBtn} ${styles.navBtnDanger}`}
                              type="button"
                              disabled={busyId === u.id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (busyId) return;
                                setBusyId(u.id);
                                setMsg("");
                                try {
                                  await csAdminForceRemoveGroup(u.id);
                                  await loadGroupsForUsers(list);
                                } catch (err) {
                                  setMsg(err?.message || "Əməliyyat alınmadı.");
                                } finally {
                                  setBusyId(null);
                                }
                              }}
                            >
                              {busyId === u.id ? "..." : "Çıxart"}
                            </button>
                          ) : (
                            <button
                              className={styles.navBtn}
                              type="button"
                              disabled={busyId === u.id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (busyId) return;
                                setBusyId(u.id);
                                setMsg("");
                                try {
                                  await csAdminForceAddGroup(u.id);
                                  await loadGroupsForUsers(list);
                                } catch (err) {
                                  setMsg(err?.message || "Əməliyyat alınmadı.");
                                } finally {
                                  setBusyId(null);
                                }
                              }}
                            >
                              {busyId === u.id ? "..." : "Əlavə et"}
                            </button>
                          )}
                        </td>

                        <td className={styles.td}>
                          <button
                            className={styles.navBtn}
                            type="button"
                            onClick={() => goDetail(u.id)}
                          >
                            Aç
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.cards}>
            {filteredList.length === 0 ? (
              <div className={styles.muted}>
                {loading || subLoading || groupLoading
                  ? "Yüklənir..."
                  : "İstifadəçi tapılmadı."}
              </div>
            ) : (
              filteredList.map((u) => {
                const phone = u?.phone;
                const accOk = accountActiveBool(u);
                const hasSub = subHasByPhone(phone);
                const inGroup = groupStatusByPhone(phone);

                return (
                  <div
                    key={u.id}
                    className={styles.userCard}
                    role="button"
                    tabIndex={0}
                    onClick={() => goDetail(u.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") goDetail(u.id);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className={styles.cardTop}>
                      <div className={styles.name}>
                        {u.firstName} {u.lastName}
                      </div>
                      <span className={verifyPillClass(styles, u.verifyStatus)}>
                        {azVerifyStatusLabel(u.verifyStatus)}
                      </span>
                    </div>

                    <div className={styles.row}>
                      <div className={styles.key}>Telefon</div>
                      <div>{displayPhoneAz(phone)}</div>
                    </div>

                    <div className={styles.row}>
                      <div className={styles.key}>Hesab</div>
                      <div>
                        <span className={pillOkBad(styles, accOk)}>
                          {accountActiveLabel(u)}
                        </span>
                      </div>
                    </div>

                    <div className={styles.row}>
                      <div className={styles.key}>Abunəlik</div>
                      <div>
                        {hasSub === null ? (
                          <span className={styles.muted}>
                            {subLabelByPhone(phone)}
                          </span>
                        ) : (
                          <span className={pillOkBad(styles, hasSub)}>
                            {subLabelByPhone(phone)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={styles.row}>
                      <div className={styles.key}>WhatsApp</div>
                      <div>
                        {inGroup === null ? (
                          "Yoxlanılır..."
                        ) : inGroup ? (
                          <button
                            className={`${styles.navBtn} ${styles.navBtnDanger}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              (async () => {
                                if (busyId) return;
                                setBusyId(u.id);
                                setMsg("");
                                try {
                                  await csAdminForceRemoveGroup(u.id);
                                  await loadGroupsForUsers(list);
                                } catch (err) {
                                  setMsg(err?.message || "Əməliyyat alınmadı.");
                                } finally {
                                  setBusyId(null);
                                }
                              })();
                            }}
                          >
                            {busyId === u.id ? "..." : "Çıxart"}
                          </button>
                        ) : (
                          <button
                            className={styles.navBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              (async () => {
                                if (busyId) return;
                                setBusyId(u.id);
                                setMsg("");
                                try {
                                  await csAdminForceAddGroup(u.id);
                                  await loadGroupsForUsers(list);
                                } catch (err) {
                                  setMsg(err?.message || "Əməliyyat alınmadı.");
                                } finally {
                                  setBusyId(null);
                                }
                              })();
                            }}
                          >
                            {busyId === u.id ? "..." : "Əlavə et"}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className={styles.row}>
                      <div className={styles.key}>ID</div>
                      <div>{u.id}</div>
                    </div>

                    <div className={styles.link} style={{ pointerEvents: "none" }}>
                      Ətraflı bax
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
